/**
 * Step 1 › where the role is and how it works (Sofia, Oct 26 2026). Shows the sentence every InMail
 * of this role gets — written by code from the brief, not by the AI — and lets the recruiter set
 * office / hybrid / remote, the city and office days when the brief reads wrong or unclear. Saved
 * on the brief (`where`), kept when the brief is rebuilt; "As the brief says" clears it.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { BriefDoc, WhereMode, WhereSetting } from '../../types/taOutreach';

const AUTO = 'auto';
const fieldCls = 'h-8 rounded-md border border-solid border-[#E9EAEC] bg-white px-2 text-[13px]';

export function WhereEditor({ brief, onSave }: { brief: BriefDoc; onSave: (where: WhereSetting | null) => void | Promise<void> }) {
  const view = brief.whereView;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<WhereMode | typeof AUTO>(brief.where?.mode ?? AUTO);
  const [city, setCity] = useState(brief.where?.city ?? view?.cities.join(' or ') ?? '');
  const [days, setDays] = useState(String(brief.where?.officeDays ?? view?.officeDays ?? ''));
  const [officeOptional, setOfficeOptional] = useState(brief.where?.officeOptional ?? view?.officeOptional ?? false);
  const [busy, setBusy] = useState(false);
  if (!view) return null;
  const sentence = view.sentence[brief.language];

  async function save() {
    setBusy(true);
    try {
      await onSave(
        mode === AUTO
          ? null
          : { mode, city: city.trim() || null, officeDays: mode === 'hybrid' && days ? Number(days) : null, area: null, officeOptional: mode === 'remote' && officeOptional }
      );
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 text-[13px]" data-testid="where-editor">
      <span className="block text-xs font-semibold text-[#707070]">
        Where the role is · {view.fromRecruiter ? 'set by you' : 'from the brief'}
        {!open && (
          <button type="button" className="ml-2 font-semibold text-[#0072EA] underline" onClick={() => setOpen(true)}>
            Change
          </button>
        )}
      </span>
      {sentence ? (
        <p className="m-0 mt-1">“{sentence}”</p>
      ) : (
        <p className="m-0 mt-1 text-[#7A4B00]">{view.unclear || 'The brief says nothing about it.'} Until then, the AI writes this sentence in each InMail.</p>
      )}
      {open && (
        <div className="mt-2 flex flex-wrap items-end gap-3 rounded-md border border-solid border-[#E9EAEC] bg-[#F8F8F8] p-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[#707070]">How it works</span>
            <select aria-label="How it works" className={fieldCls} value={mode} onChange={(e) => setMode(e.target.value as WhereMode | typeof AUTO)}>
              <option value={AUTO}>As the brief says</option>
              <option value="onsite">In the office</option>
              <option value="hybrid">Hybrid</option>
              <option value="remote">Remote</option>
            </select>
          </label>
          {mode !== AUTO && (
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[#707070]">{mode === 'remote' ? 'Office city (optional)' : 'City'}</span>
              <input aria-label="City" className={fieldCls} value={city} placeholder="Munich" onChange={(e) => setCity(e.target.value)} />
            </label>
          )}
          {mode === 'hybrid' && (
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[#707070]">Office days a week</span>
              <select aria-label="Office days" className={fieldCls} value={days} onChange={(e) => setDays(e.target.value)}>
                <option value="">Not fixed</option>
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={String(n)}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          )}
          {mode === 'remote' && (
            <label className="flex items-center gap-2 pb-1">
              <input type="checkbox" checked={officeOptional} onChange={(e) => setOfficeOptional(e.target.checked)} />
              <span>They may also work from the office</span>
            </label>
          )}
          <span className="flex gap-2">
            <Button size="sm" variant="primary" className="rounded-md" disabled={busy || (mode === 'onsite' && !city.trim())} onClick={() => void save()}>
              Save
            </Button>
            <Button size="sm" variant="ghost" className="rounded-md" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </span>
          <span className="basis-full text-xs text-[#707070]">Open drafts of this role are updated; drafts you edited by hand keep their text.</span>
        </div>
      )}
    </div>
  );
}
